import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { AchievementsPage } from '../ui/diary/AchievementsPage';
import type {
  AchievementsResponse,
  AchievementsPageLabels,
} from '../shared/types/achievements';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

export interface AchievementsPageContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  goalsUrl: string;
  highlightAchievement: string | null;
  labels: AchievementsPageLabels;
}

const GET_ACHIEVEMENTS_QUERY = `
  query GetAchievements {
    achievements {
      groups {
        title
        achievements {
          key
          name
          description
          icon
          level
          leveled
          unlocked
          unlockedAt
          unlockedFeatures {
            key
            name
          }
        }
      }
      totalCount
      unlockedCount
      progressPercentage
    }
  }
`;

function AchievementsPageInner({
  graphqlEndpoint,
  csrfToken,
  goalsUrl,
  highlightAchievement,
  labels,
}: AchievementsPageContainerProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['achievements'],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_ACHIEVEMENTS_QUERY,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch achievements');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.achievements as AchievementsResponse;
    },
  });

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <AchievementsPage
      groups={data.groups}
      totalCount={data.totalCount}
      unlockedCount={data.unlockedCount}
      progressPercentage={data.progressPercentage}
      goalsUrl={goalsUrl}
      highlightAchievement={highlightAchievement}
      labels={labels}
    />
  );
}

export function AchievementsPageContainer(props: AchievementsPageContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AchievementsPageInner {...props} />
    </QueryClientProvider>
  );
}

export function mountAchievementsPage(
  container: HTMLElement,
  props: AchievementsPageContainerProps
) {
  // Clean up existing root if any (for Turbo navigation)
  const existingRoot = (container as any).__reactRoot;
  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = createRoot(container);
  (container as any).__reactRoot = root;
  root.render(<AchievementsPageContainer {...props} />);
  return root;
}

if (typeof window !== 'undefined') {
  (window as any).mountAchievementsPage = mountAchievementsPage;
}

export default AchievementsPageContainer;
